import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Key,
  Smartphone,
  Globe,
  CheckCircle,
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import { whatsappService } from '../../../../services/whatsappService';

const IntegrationStatus = ({ settings, onSettingsUpdate }) => {
  const [formData, setFormData] = useState({
    api_key: settings?.api_key || '',
    webhook_url: settings?.webhook_url || '',
    phone_number_id: settings?.phone_number_id || '',
    business_account_id: settings?.business_account_id || '',
    access_token: settings?.access_token || '',
    auto_reminders: settings?.auto_reminders || true,
    reminder_hours_before: settings?.reminder_hours_before || 24
  });

  const [showTokens, setShowTokens] = useState({
    api_key: false,
    access_token: false
  });

  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleTokenVisibility = (field) => {
    setShowTokens(prev => ({
      ...prev,
      [field]: !prev?.[field]
    }));
  };

  const testConnection = async () => {
    if (!formData?.access_token || !formData?.phone_number_id) {
      setConnectionStatus({
        success: false,
        message: 'Please provide access token and phone number ID'
      });
      return;
    }

    setTestLoading(true);
    setConnectionStatus(null);

    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 2000));

      const isValid = formData?.access_token?.startsWith('EAA') && formData?.phone_number_id?.length > 10;

      setConnectionStatus({
        success: isValid,
        message: isValid
          ? 'Connection successful! WhatsApp API is ready.' : 'Connection failed. Please check your credentials.'
      });
    } catch (error) {
      setConnectionStatus({
        success: false,
        message: 'Connection test failed. Please try again.'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setSaveMessage('');

    try {
      console.log('Saving WhatsApp settings:', formData);

      const { data, error } = await whatsappService?.updateSettings({
        ...formData,
        status: connectionStatus?.success ? 'active' : 'configured'
      });

      console.log('Save result:', { data, error });

      if (error) {
        throw new Error(error);
      }

      onSettingsUpdate(data);
      setSaveMessage('Settings saved successfully!');

      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
      setSaveMessage(`Error: ${error?.message || 'Failed to save settings. Please check your permissions.'}`);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const configurationSteps = [
    {
      title: 'WhatsApp Business Account',
      description: 'Set up your WhatsApp Business Account and get verified',
      status: formData?.business_account_id ? 'completed' : 'pending'
    },
    {
      title: 'API Credentials',
      description: 'Generate API keys and access tokens from Meta Business',
      status: formData?.access_token && formData?.api_key ? 'completed' : 'pending'
    },
    {
      title: 'Phone Number Registration',
      description: 'Register and verify your business phone number',
      status: formData?.phone_number_id ? 'completed' : 'pending'
    },
    {
      title: 'Webhook Configuration',
      description: 'Configure webhook URL for receiving message updates',
      status: formData?.webhook_url ? 'completed' : 'pending'
    },
    {
      title: 'Connection Test',
      description: 'Test the connection to ensure everything works correctly',
      status: connectionStatus?.success ? 'completed' : 'pending'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Configuration Status Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="flex items-center space-x-4 mb-6">
          <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
            <Settings className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Integration Configuration</h2>
            <p className="text-gray-400">Set up your WhatsApp Business API integration</p>
          </div>
        </div>

        {/* Setup Progress */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {configurationSteps?.map((step, index) => (
            <div key={index} className="relative">
              <div className={`p-4 rounded-xl border-2 transition-all ${step?.status === 'completed'
                ? 'border-green-500/50 bg-green-500/10' : 'border-gray-600 bg-gray-800/50'
                }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg ${step?.status === 'completed'
                    ? 'bg-green-500/20 text-green-400' : 'bg-gray-600/20 text-gray-400'
                    }`}>
                    {step?.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                  </div>
                  <span className="text-xs text-gray-500">Step {index + 1}</span>
                </div>
                <h4 className="font-semibold text-white text-sm mb-1">{step?.title}</h4>
                <p className="text-xs text-gray-400">{step?.description}</p>
              </div>

              {index < configurationSteps?.length - 1 && (
                <div className="hidden md:block absolute top-8 -right-2 w-4 h-0.5 bg-gray-600"></div>
              )}
            </div>
          ))}
        </div>
      </motion.div>
      {/* Configuration Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* API Configuration */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center space-x-3 mb-6">
            <Key className="w-6 h-6 text-yellow-400" />
            <h3 className="text-xl font-semibold text-white">API Credentials</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Business Account ID
              </label>
              <input
                type="text"
                value={formData?.business_account_id}
                onChange={(e) => handleInputChange('business_account_id', e?.target?.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                placeholder="Enter your Business Account ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number ID
              </label>
              <input
                type="text"
                value={formData?.phone_number_id}
                onChange={(e) => handleInputChange('phone_number_id', e?.target?.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                placeholder="Enter your Phone Number ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showTokens?.api_key ? "text" : "password"}
                  value={formData?.api_key}
                  onChange={(e) => handleInputChange('api_key', e?.target?.value)}
                  className="w-full px-4 py-3 pr-12 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  placeholder="Enter your API Key"
                />
                <button
                  type="button"
                  onClick={() => toggleTokenVisibility('api_key')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showTokens?.api_key ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Access Token
              </label>
              <div className="relative">
                <input
                  type={showTokens?.access_token ? "text" : "password"}
                  value={formData?.access_token}
                  onChange={(e) => handleInputChange('access_token', e?.target?.value)}
                  className="w-full px-4 py-3 pr-12 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  placeholder="Enter your Access Token"
                />
                <button
                  type="button"
                  onClick={() => toggleTokenVisibility('access_token')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showTokens?.access_token ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Webhook & Settings */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center space-x-3 mb-6">
            <Globe className="w-6 h-6 text-blue-400" />
            <h3 className="text-xl font-semibold text-white">Webhook & Settings</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Webhook URL
              </label>
              <input
                type="url"
                value={formData?.webhook_url}
                onChange={(e) => handleInputChange('webhook_url', e?.target?.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                placeholder="https://yourapp.com/api/whatsapp/webhook"
              />
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData?.auto_reminders}
                  onChange={(e) => handleInputChange('auto_reminders', e?.target?.checked)}
                  className="w-5 h-5 text-blue-500 bg-white/5 border-white/20 rounded focus:ring-blue-500/50 focus:ring-2"
                />
                <span className="text-gray-300">Enable automatic appointment reminders</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Reminder Hours Before Appointment
              </label>
              <select
                value={formData?.reminder_hours_before}
                onChange={(e) => handleInputChange('reminder_hours_before', parseInt(e?.target?.value))}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
              >
                <option value={1}>1 hour before</option>
                <option value={2}>2 hours before</option>
                <option value={6}>6 hours before</option>
                <option value={12}>12 hours before</option>
                <option value={24}>24 hours before</option>
                <option value={48}>48 hours before</option>
              </select>
            </div>

            {/* Connection Test */}
            <div className="pt-4">
              <button
                onClick={testConnection}
                disabled={testLoading || !formData?.access_token || !formData?.phone_number_id}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center space-x-2"
              >
                {testLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Testing Connection...</span>
                  </>
                ) : (
                  <>
                    <Smartphone className="w-5 h-5" />
                    <span>Test Connection</span>
                  </>
                )}
              </button>
            </div>

            {/* Connection Status */}
            {connectionStatus && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl border ${connectionStatus?.success
                  ? 'border-green-500/50 bg-green-500/10 text-green-400' : 'border-red-500/50 bg-red-500/10 text-red-400'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  {connectionStatus?.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <span className="font-medium">{connectionStatus?.message}</span>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
      {/* Save Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex justify-end space-x-4"
      >
        {saveMessage && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`px-4 py-2 rounded-xl ${saveMessage?.includes('Error')
              ? 'bg-red-500/10 text-red-400 border border-red-500/50' : 'bg-green-500/10 text-green-400 border border-green-500/50'
              }`}
          >
            {saveMessage}
          </motion.div>
        )}

        <button
          onClick={saveSettings}
          disabled={loading}
          className="px-8 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
        >
          {loading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Save Settings</span>
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
};

export default IntegrationStatus;